import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Functions } from '@angular/fire/functions';
import { Platform } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Big } from "big.js";
import { OrderModel } from "src/app/models/order.model";
import { IPayment, IPaymentCard, IPaymentConfig } from "src/app/models/payment.model";
import { ProfileModel } from "src/app/models/profile.model";
import { TourneeModel } from "src/app/models/tournee.model";
import { DataService } from "../data/data.service";
import { DateService } from "../date/date.service";
import { FileService } from "../file/file.service";
import { MathService } from "../math/math.service";
import { ShareService } from "../share/share.service";

@Injectable({ providedIn: "root" })
export class PaymentService {
  private fns: Functions = inject(Functions);

  statuses = {
    open: {name:'created',status:false, icon:'notifications-outline', color:'primary', 
      label:'created'},
    unpaid: {name:'unpaid',status:false, icon:'checkmark-outline', color:'secondary', 
      label:'accepted'},
    paid: {name:'paid',status:false, icon:'checkmark-done-outline', color:'secondary', 
      label:'prepared'},
  }
  modes = ['paylib','lydia','payconiq','cash','card','transfer','mangopay', 'check'];

  statusesArray = Object.values(this.statuses);


  geocodingTypeValidForPayment = ['street','housenumber'];

  constructor(public platform: Platform, public http: HttpClient, private shareService:ShareService, private dateService:DateService,
    private translateService:TranslateService, private dataService: DataService, private fileService:FileService, private mathService:MathService) {
  }

  activatePayment(config:IPaymentConfig): Promise<any> {
    return this.dataService.callFunction("payment-activatePayment",config);
  }

  makePayment(paymentMethod, order: OrderModel): Promise<IPayment> {
    return this.dataService.callFunction("payment-makePayment",{ order: order, payment: paymentMethod }) as Promise<IPayment>;
  }

  async initiatePayment(payment:IPayment,order:OrderModel, platform:string):Promise<IPayment>{
    const result = await this.dataService.callFunction("payment-initiate",{payment:payment,order:order, platform:platform});
    return result.data as IPayment
  }

  preRegisterCard(payment:IPayment):Promise<IPaymentCard>{
    return this.dataService.callFunction("payment-preRegisterCard",{payment}) as Promise<IPaymentCard>;
  }

  async validatePayment(basketKey:string,paymentKey?:string):Promise<OrderModel>{
    const result = await this.dataService.callFunction("payment-validate",{basketKey:basketKey,paymentKey:paymentKey});
    return result.data  as Promise<OrderModel>;
  }

  async sendPaymentRequestByEmail(payment:IPayment,order:OrderModel,tournee:TourneeModel,platform:string){
    order.tournee = tournee;
    order.producer = tournee.producer;
    const paymentResult = await this.initiatePayment(payment,order,platform);
    console.log(paymentResult);
    const subject = `${this.translateService.instant('payment.RequestForOrderOf')} ${this.dateService.formatToShortDate(tournee.details.date)}`;
    const title = `${this.translateService.instant('payment.RequestForOrderOf')} ${this.dateService.formatToShortDate(tournee.details.date)}`;
    const photoURL = this.fileService.getPhotoUrl(tournee.producer.details.shortDetails.photoURL,300,tournee.producer.details.shortDetails.business);
    const subtitle = '';
    const content = `
                    <p>${this.translateService.instant('common.Hello')} ${order.consumer.details.fullDetails.firstname},</p>
                    <p>${this.translateService.instant('payment.RequestForOrderOf')} ${this.dateService.formatToShortDate(tournee.details.date)}</p>`;

    const body = `
    ${this.translateService.instant('common.Hello')} ${order.consumer.details.fullDetails.firstname},%0D%0A%0D%0A
    ${this.translateService.instant('payment.RequestPaymentLydiaText')}%0D%0A%0D%0A
    ${paymentResult.redirectURL} %0D%0A%0D%0A
    ${this.translateService.instant('common.ThankYou')} %0D%0A%0D%0A
    ${tournee.producer.details.shortDetails.name}
    `;
    
    this.shareService.sendEmail(order.consumer.details.fullDetails.email,subject,body);
  }
  async sendPaymentRequestForMarketByEmail(payment:IPayment,order:OrderModel,platform:string){
    const paymentResult = await this.initiatePayment(payment,order,platform);
    console.log(paymentResult);
    const subject = `${this.translateService.instant('payment.RequestForOrderOf')} ${this.dateService.formatToShortDate(order.details.datePlace.date)}`;
    const title = `${this.translateService.instant('payment.RequestForOrderOf')} ${this.dateService.formatToShortDate(order.details.datePlace.date)}`;
    const photoURL = this.fileService.getPhotoUrl(order.market.details.photoURL,300,'market');
    const subtitle = '';
    const content = `
                    <p>${this.translateService.instant('common.Hello')} ${order.consumer.details.fullDetails.firstname},</p>
                    <p>${this.translateService.instant('payment.RequestForOrderOf')} ${this.dateService.formatToShortDate(order.details.datePlace.date)}</p>`;
    const body = `
    ${this.translateService.instant('common.Hello')} ${order.consumer.details.fullDetails.firstname},%0D%0A%0D%0A
    ${this.translateService.instant('payment.RequestPaymentLydiaText')}%0D%0A%0D%0A
    ${paymentResult.redirectURL} %0D%0A%0D%0A
    ${this.translateService.instant('common.ThankYou')} %0D%0A%0D%0A
    ${order.market.owner.details.shortDetails.name}
    `;
    
    this.shareService.sendEmail(order.consumer.details.fullDetails.email,subject,body);
  }

  async sendPaymentReminderByEmail(payment:IPayment,order:OrderModel){
    order.producer = order.producer || order.tournee.producer;
    const subject = `${this.translateService.instant('payment.Reminder.Subject')} ${this.dateService.formatToShortDate(order.tournee.details.date)}`;
    const title = `${this.translateService.instant('payment.Reminder.Subject')} ${this.dateService.formatToShortDate(order.tournee.details.date)}`;
    const photoURL = this.fileService.getPhotoUrl(order.producer.details.shortDetails.photoURL,300,order.tournee.producer.details.shortDetails.business);
    const body = `
    ${this.translateService.instant('common.Hello')} ${order.consumer.details.fullDetails.firstname},%0D%0A%0D%0A
    ${this.translateService.instant('payment.Reminder.Body')}%0D%0A%0D%0A
    ${this.translateService.instant('payment.Reminder.'+payment.mode)}%0D%0A%0D%0A
    ${payment.mode=='lydia'?payment.redirectURL:payment.bankAccount.IBAN} %0D%0A%0D%0A
    ${this.translateService.instant('common.ThankYou')} %0D%0A%0D%0A
    ${order.producer.details.shortDetails.name}
    `;
    this.shareService.sendEmail(order.consumer.details.fullDetails.email,subject,body);
  }

  async changeOrderPaymentStatus(order: OrderModel, tournee:TourneeModel, producer: ProfileModel, status: string): Promise<void> {
    const oldPaymentStatus = order.paymentStatus;
    order = this.updatePaymentStatus(order);
    if(order.paymentStatus !== oldPaymentStatus){
      this.dataService.updateOrderPaymentStatusByProducer(order, producer);
    }
  }

  updatePaymentStatus(order:OrderModel){
    const payments = Object.entries(order.payments);
    let paidBig = new Big(0);
    for(const [key,value] of payments){
      const payment:IPayment = value;
      paidBig = paidBig.plus(new Big(payment.debitedFunds.amount));
    }
    order.finances.totals.paid = this.mathService.bigToFloat(paidBig);
    if(order.finances.totals.paid == order.finances.totals.sold){
      order.paymentStatus = 'cashedIn';
    }
    return order;
  }
}
